import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cloud, Laptop } from 'lucide-react';
import { Modal } from './ui/Modal';
import { CONSENT_NEEDED_EVENT, keepOnDevice, storeInCloud } from '../services/vaultCloudConsent';
import { ensureVaultSync } from '../services/vaultSync';
import { recordFeature, VAULT } from '../services/telemetry';
import './Vault.css';

/**
 * The cloud-copy question (Standalone vault card 6.3', owner decision
 * 08.10.2026): every account answers it once, paid accounts that already
 * sync included. Opened by `ensureVaultSync` (window event) while the answer
 * is missing; the choice can be changed later in Settings → Extensions →
 * Vault. Closing it without answering is allowed — it asks again next time —
 * and changes nothing.
 */
export default function VaultCloudConsentDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Asked at most once per app run; Settings is the way back.
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    const onNeeded = () => {
      if (!asked) setOpen(true);
    };
    window.addEventListener(CONSENT_NEEDED_EVENT, onNeeded);
    return () => window.removeEventListener(CONSENT_NEEDED_EVENT, onNeeded);
  }, [asked]);

  const choose = async (fn, metric) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      recordFeature(metric); // card 7.2: which choice, never what is in the vault
      setAsked(true);
      setOpen(false);
      ensureVaultSync();
    } catch {
      setError(t('vault.cloudChoiceFailed', 'That did not work. Check your connection and try again.'));
    }
    setBusy(false);
  };

  const close = () => {
    if (busy) return;
    setAsked(true);
    setOpen(false);
  };

  return (
    <Modal isOpen={open} onClose={close} size="lg" title={t('vault.cloudTitle', 'Where should your vault live?')}>
      <div className="vault-pass-body" data-testid="vault-cloud-consent">
        <p className="vault-pass-text">
          {t(
            'vault.cloudIntro',
            'Your vault is end-to-end encrypted: only your master passphrase opens it. Apiboo can keep an encrypted copy in your account so it is on all your devices.'
          )}
        </p>
        <button type="button" className="vault-cloud-choice" disabled={busy} onClick={() => choose(storeInCloud, VAULT.CLOUD_CHOSEN)}>
          <Cloud size={18} />
          <span className="vault-cloud-choice-texts">
            <span className="vault-cloud-choice-title">
              {t('vault.cloudStore', 'Store encrypted in my Apiboo cloud (recommended)')}
            </span>
            <span className="vault-pass-hint">
              {t(
                'vault.cloudStoreHelp',
                'Needed for the browser extension without the desktop app, the Android app and the web vault. Apiboo stores only ciphertext and cannot read it.'
              )}
            </span>
          </span>
        </button>
        <button type="button" className="vault-cloud-choice" disabled={busy} onClick={() => choose(keepOnDevice, VAULT.DEVICE_ONLY)}>
          <Laptop size={18} />
          <span className="vault-cloud-choice-texts">
            <span className="vault-cloud-choice-title">{t('vault.cloudDevice', 'Keep on this device only')}</span>
            <span className="vault-pass-hint">
              {t(
                'vault.cloudDeviceHelp',
                'Nothing is uploaded. The browser extension works through this app while it runs. You can change this later in Settings.'
              )}
            </span>
          </span>
        </button>
        {error && <div className="vault-modal-error">{error}</div>}
        {/* Card 7.2: the first-run explainer — two secrets, what the server keeps, what cannot be undone. */}
        <details className="vault-cloud-explainer" data-testid="vault-cloud-explainer">
          <summary>{t('vault.cloudHowTitle', 'How this works')}</summary>
          <ul>
            <li>
              {t(
                'vault.cloudHowTwoSecrets',
                'Two different secrets: your Apiboo sign-in lets a device download the encrypted vault; only your master password decrypts it, on the device.'
              )}
            </li>
            <li>
              {t(
                'vault.cloudHowServer',
                'Apiboo stores only ciphertext and what is needed to sync it (encryption settings, a key check, item versions). Never your master password, your items or their names.'
              )}
            </li>
            <li>
              {t(
                'vault.cloudHowLoss',
                'If you lose BOTH your master password and your 12-word recovery phrase, nobody — not Apiboo either — can open the vault again. Keep the recovery phrase somewhere safe.'
              )}
            </li>
          </ul>
        </details>
      </div>
    </Modal>
  );
}
